import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Loader2, FileText, FileSpreadsheet } from 'lucide-react';
import { API_ENDPOINTS } from '../config';

const API = API_ENDPOINTS.main;

// File type icons
const FILE_TYPE_ICONS = {
  '.csv': FileSpreadsheet,
  '.xlsx': FileSpreadsheet,
  '.xls': FileSpreadsheet,
  '.json': FileText,
};

export default function FilePreviewModal({ file, onClose, isOpen }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Fetch preview data when modal opens
  useEffect(() => {
    if (isOpen && file) {
      fetchPreview();
    }
  }, [isOpen, file]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewData(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/files/preview/${file.id}?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to preview file');
      }

      const data = await response.json();
      setPreviewData(data);
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !file) return null;

  const FileIcon = FILE_TYPE_ICONS[file.file_type] || FileText;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Modal */}
        <div
          className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-700 m-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-purple-700">
            <div className="flex items-center gap-3">
              <FileIcon className="w-5 h-5 text-white" />
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {file.original_filename}
                </h2>
                <p className="text-xs text-white/80">
                  File Preview (first 100 rows)
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {loading && (
              <div className="flex flex-col items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Loading preview...
                </p>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                      Preview Error
                    </h3>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              </div>
            )}

            {previewData && !loading && !error && (
              <div className="space-y-4">
                {/* File Info */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Rows Shown:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                        {previewData.row_count}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Columns:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                        {previewData.columns?.length || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">File Type:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                        {file.file_type.toUpperCase().replace('.', '')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Size:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                        {(file.file_size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                </div>

                {/* Column Info */}
                {previewData.columns && previewData.columns.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Column Types
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {previewData.columns.map((col, idx) => (
                        <div
                          key={idx}
                          className="bg-white dark:bg-gray-900 rounded p-3 border border-gray-200 dark:border-gray-700"
                        >
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-1 truncate">
                            {col.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Type: <span className="font-mono">{col.type}</span>
                          </div>
                          {col.sample_value !== null && col.sample_value !== undefined && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Sample: <span className="font-mono italic">{String(col.sample_value)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Table */}
                {previewData.rows && previewData.rows.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-100 dark:bg-gray-800">
                              #
                            </th>
                            {previewData.columns?.map((col, idx) => (
                              <th
                                key={idx}
                                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                              >
                                {col.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {previewData.rows.map((row, rowIdx) => (
                            <tr
                              key={rowIdx}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            >
                              <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 font-medium sticky left-0 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800">
                                {rowIdx + 1}
                              </td>
                              {previewData.columns?.map((col, colIdx) => {
                                const value = row[col.name];
                                const displayValue = value === null || value === undefined
                                  ? <span className="text-gray-400 dark:text-gray-500 italic">null</span>
                                  : String(value);

                                return (
                                  <td
                                    key={colIdx}
                                    className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap max-w-xs truncate"
                                    title={String(value)}
                                  >
                                    {displayValue}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Footer Note */}
                {previewData.rows && previewData.rows.length === 100 && (
                  <div className="text-center py-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                      Showing first 100 rows. Full dataset available for analysis in chat.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
